import React from 'react';
import { Pressable, View } from 'react-native';

import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { Section, useLanding } from '@/components/Landing/LandingKit';
import { EASE, fluid, isWeb } from '@/components/Landing/landingTokens';
import { BILLING, HERO_TRUST } from '@/constants/pricingContent';

/** Billing controls sit directly above the plan comparison. */
function BillingToggle({ isAnnual, onChange }) {
  const { c } = useLanding();

  const option = (annual, label) => {
    const active = annual === isAnnual;
    return (
      <Pressable
        onPress={() => onChange(annual)}
        accessibilityRole="radio"
        accessibilityState={{ checked: active }}
        {...(isWeb ? { 'aria-checked': active } : {})}
        accessibilityLabel={`${label} billing`}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 8,
          paddingHorizontal: 16,
          paddingVertical: 12,
          borderRadius: 6,
          backgroundColor: active ? (c.isDark ? 'rgba(247,245,241,0.10)' : '#FFFFFF') : 'transparent',
          ...(isWeb ? {
            cursor: 'pointer',
            transition: `background-color 220ms ${EASE}`,
            boxShadow: active && !c.isDark ? '0 1px 3px rgba(20,17,14,0.10)' : 'none',
          } : {}),
        }}
      >
        <Text weight="bold" style={{ color: active ? c.ink : c.muted, fontSize: 12.5, letterSpacing: -0.1 }}>
          {label}
        </Text>
        {annual ? (
          <Text weight="bold" style={{ color: c.accent, fontSize: 11 }}>
            Save {BILLING.savingsPercent}%
          </Text>
        ) : null}
      </Pressable>
    );
  };

  return (
    <View
      accessibilityRole={isWeb ? 'radiogroup' : undefined}
      accessibilityLabel="Billing period"
      style={{
        flexDirection: 'row',
        alignSelf: 'flex-start',
        padding: 4,
        borderRadius: 6,
        borderWidth: 1,
        borderColor: c.rule,
        backgroundColor: c.panel,
      }}
    >
      {option(false, 'Monthly')}
      {option(true, 'Annual')}
    </View>
  );
}

/** Masthead: the promise, the billing cadence, and what it costs to try. */
function PricingHero({ topInset = 0, isAnnual, onBillingChange }) {
  const { c, isXS, isCompact, width } = useLanding();
  const size = fluid(width, 40, 68);

  return (
    <Section
      label="Pricing"
      top={false}
      contentStyle={{
        paddingTop: (isXS ? topInset : 0) + fluid(width, 108, 152),
        paddingBottom: 24,
      }}
    >
      <Text weight="medium" style={{ color: c.accent, fontSize: 14, marginBottom: 26 }}>Pricing</Text>
      <View style={{ flexDirection: isCompact ? 'column' : 'row', alignItems: isCompact ? 'stretch' : 'flex-end', gap: isCompact ? 26 : 80 }}>
        <View style={{ flex: isCompact ? undefined : 1.2, minWidth: 0 }}>
          <Text tag="h1" weight="semibold" style={{ color: c.ink, fontSize: size, lineHeight: Math.round(size * 1.06), letterSpacing: -size * 0.045, ...(isWeb ? { whiteSpace: 'pre-line' } : {}) }}>
            {'From prompt to\nfinished work.'}
          </Text>
        </View>
        <Text tag="p" style={{ flex: isCompact ? undefined : 1, minWidth: 0, color: c.inkSoft, fontSize: 17, lineHeight: 28, maxWidth: 450 }}>
          Start free. Upgrade to Plus for Work mode: AI agents that research,
          use tools, and deliver finished work. Get leading models, creative
          studios, and monthly credits in one plan.
        </Text>
      </View>

      <View style={{ flexDirection: isCompact ? 'column' : 'row', alignItems: isCompact ? 'flex-start' : 'center', justifyContent: 'space-between', gap: 22, marginTop: fluid(width, 32, 48), paddingTop: 24, borderTopWidth: 1, borderTopColor: c.rule }}>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', columnGap: 20, rowGap: 12 }}>
          {HERO_TRUST.map((item) => (
            <View key={item} style={{ flexDirection: 'row', alignItems: 'center', gap: 7 }}>
              <Icon name="check" size={10} color={c.accent} solid />
              <Text style={{ color: c.muted, fontSize: 12.5 }}>{item}</Text>
            </View>
          ))}
        </View>
        <BillingToggle isAnnual={isAnnual} onChange={onBillingChange} />
      </View>
    </Section>
  );
}

export default React.memo(PricingHero);
